import type React from 'react'
import type { JSX } from 'react'
import type { ICard } from '../Card'

import { ButtonIconSizes } from 'atoms/Button'
import { InlinePlaySolidButton } from 'atoms/Button/Templates'
import { FallbackImageSizes } from 'atoms/FallbackImage'
import { IconNames } from 'atoms/Icon'
import { TextBox, TextBoxTypes } from 'atoms/TextBox'
import { Colors, TextAligning } from 'atoms/TextBox/TextBox.h'
import { useBooleanState, useClientWidth } from 'utils/hooks'
import { COLOR } from 'vars/index'
import { PosterContainerContentSC } from '../base-card'
import { Card } from '../Card'
import { CARDS_ASPECT_RATIO, CardAnimations, CardCorners } from '../Card.h'
import { useTvCardBackroundCover } from './lib'
import { TVCardEPGSC } from './TVCardEPG.styled'
import { TVCardHoverContent } from './TVCardHoverContent'
import { useTvCardHoverHandlers } from './useTvCardHoverHandlers'

export interface ITVCardEPGProps extends Omit<ICard, 'children'> {
  children: React.ReactNode
  viewedButtonFavorite?: boolean
  widthLogo?: string
  heightLogo?: string
  title?: string
  titleFont?: TextBoxTypes
  horizontalCovers?: Record<string, any>
  squareCovers?: Record<string, any>
}

export const TVCardEPG = ({
  children,
  posterUrl = '',
  aspectRatio = CARDS_ASPECT_RATIO.SQUARE,
  corners = CardCorners.CORNERS_0,
  backgroundColor = COLOR.BACKGROUND_DARK_EPG,
  backgroundColorForLogo = COLOR.SURFACE_OPACITY_DARK_25,
  childrenTransition = CardAnimations.FadeIn,
  borderVisible = true,
  height = '84px',
  width = '165px',
  viewedButtonFavorite = true,
  fallbackImageSize = FallbackImageSizes.S,
  roundedPoster = true,
  fallbackImageBackground,
  title = '',
  titleFont = TextBoxTypes.Caption3,
  horizontalCovers,
  squareCovers,
  locked,
  lockedIcon = IconNames.tvPinLock,
  hidePriceOnHover = false,
  rented,
  aclBlockedBy = false,
  backgroundColorHovered = COLOR.SURFACE_OPACITY_DARK_70,
  ...rest
}: ITVCardEPGProps): JSX.Element => {
  const { hoverContentVisible, cardRest, onMouseEnter, onMouseLeave } =
    useTvCardHoverHandlers(rest)
  const [isPosterLoaded, handlePosterLoading, handlePosterError] =
    useBooleanState(true)
  const clientWidth = useClientWidth()

  const covers = clientWidth <= 1239 ? squareCovers : horizontalCovers

  const titleComponent = (
    <TextBox
      color={Colors.TEXT_ICON_PRIMARY}
      font={titleFont}
      maxLines={2}
      title={title}
      alignment={TextAligning.Center}
    />
  )

  const poster = isPosterLoaded ? posterUrl : ''
  const BackgroundCover =
    locked || poster ? useTvCardBackroundCover(title, covers) : <></>

  const posterContent = (
    <PosterContainerContentSC>
      {!poster && titleComponent}
    </PosterContainerContentSC>
  )

  return (
    <TVCardEPGSC
      height={height}
      width={width}
      backgroundColorForLogo={backgroundColorForLogo}
    >
      <Card
        posterUrl={poster}
        aspectRatio={aspectRatio}
        corners={corners}
        backgroundColor={backgroundColor}
        childrenTransition={undefined}
        {...cardRest}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        borderVisible={borderVisible}
        fallbackImageSize={fallbackImageSize}
        roundedPoster={roundedPoster}
        fallbackImageBackground={fallbackImageBackground}
        locked={locked}
        posterContent={posterContent}
        handlePosterLoading={handlePosterLoading}
        handlePosterError={handlePosterError}
        backgroundCover={BackgroundCover}
        lockedIcon={lockedIcon}
        hidePriceOnHover={!aclBlockedBy && hidePriceOnHover}
        rented={rented}
        aclBlockedBy={aclBlockedBy}
        backgroundColorHovered={
          !aclBlockedBy ? backgroundColorHovered : COLOR.TRANSPARENT
        }
      >
        <TVCardHoverContent
          favoriteChildren={children}
          hoverContent={
            !aclBlockedBy ? (
              <InlinePlaySolidButton iconSize={ButtonIconSizes.small} />
            ) : null
          }
          viewedButtonFavorite={viewedButtonFavorite}
          aclBlockedBy={aclBlockedBy}
          childrenTransition={childrenTransition}
          hoverTrigger={hoverContentVisible}
        />
      </Card>
    </TVCardEPGSC>
  )
}
